import { useEffect, useRef, useState } from 'react';

const getScrollContainer = (element: HTMLElement): HTMLElement | null => {
    const dataGridScroller = element.closest('.MuiDataGrid-virtualScroller') as HTMLElement | null;
    if (dataGridScroller) return dataGridScroller;

    let current = element.parentElement;
    while (current) {
        const styles = window.getComputedStyle(current);
        if (styles.overflowY === 'auto' || styles.overflowY === 'scroll') return current;
        current = current.parentElement;
    }

    return null;
};

const isFullyVisibleInContainer = (element: HTMLElement, container: HTMLElement | null) => {
    const rect = element.getBoundingClientRect();
    const bounds = container
        ? container.getBoundingClientRect()
        : ({ top: 0, bottom: window.innerHeight } as Pick<DOMRect, 'top' | 'bottom'>);

    return rect.top >= bounds.top && rect.bottom <= bounds.bottom;
};

export const useGridDropdownVisibility = ({
    referenceElement,
    focused,
    onClose
}: {
    referenceElement: HTMLElement | null;
    focused: boolean;
    onClose: () => void;
}) => {
    const [isReferenceVisible, setIsReferenceVisible] = useState(true);
    const rafIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!focused || !referenceElement) return;
        const scrollContainer = getScrollContainer(referenceElement);

        const syncVisibility = () => {
            const visible = isFullyVisibleInContainer(referenceElement, scrollContainer);
            setIsReferenceVisible(visible);
            if (!visible) onClose();
        };

        const scheduleSyncVisibility = () => {
            if (rafIdRef.current !== null) return;
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                syncVisibility();
            });
        };

        syncVisibility();

        scrollContainer?.addEventListener('scroll', scheduleSyncVisibility, { passive: true });
        window.addEventListener('resize', scheduleSyncVisibility);

        return () => {
            scrollContainer?.removeEventListener('scroll', scheduleSyncVisibility);
            window.removeEventListener('resize', scheduleSyncVisibility);
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [focused, onClose, referenceElement]);

    return isReferenceVisible;
};
